"use client";

import { ContextMenu } from "@base-ui/react/context-menu";
import { IconChevronRightMedium } from "central-icons/IconChevronRightMedium";
import DemoFooter from "@/components/preview/demo-footer";
import DemoHint from "@/components/preview/demo-hint";
import DemoWrapper from "@/components/preview/demo-wrapper";
import { Badge } from "@/components/ui/badge";
import { ResultBadge } from "@/components/ui/result-badge";
import { cn } from "@/lib/utils";

const ITEM_CLASS =
  "flex cursor-default select-none items-center rounded-md px-2.5 py-1.5 text-gray-1200 text-sm outline-none data-highlighted:bg-gray-300";
const SEPARATOR_CLASS = "-mx-1 my-1 h-px bg-gray-300";

const POPUP_BASE =
  "z-50 min-w-44 origin-(--transform-origin) rounded-lg bg-preview-bg p-1 text-gray-1200 shadow-custom outline-none";
const POPUP_ANIMATED =
  "transition-[transform,scale,opacity] duration-300 ease-out will-change-transform data-starting-style:scale-95 data-starting-style:opacity-0 data-ending-style:scale-95 data-ending-style:opacity-0 motion-reduce:transition-none";
const POPUP_EXIT_ONLY =
  "transition-[transform,scale,opacity] duration-150 ease-out will-change-transform data-ending-style:scale-95 data-ending-style:opacity-0 motion-reduce:transition-none";

function MenuItems({ animated }: { animated: boolean }) {
  const itemClass = cn(
    ITEM_CLASS,
    animated && "transition-colors duration-200 ease-out"
  );
  return (
    <>
      <ContextMenu.Item className={itemClass}>New folder</ContextMenu.Item>
      <ContextMenu.Separator className={SEPARATOR_CLASS} />
      <ContextMenu.Item className={itemClass}>Get info</ContextMenu.Item>
      <ContextMenu.Item className={itemClass}>Use stacks</ContextMenu.Item>
      <ContextMenu.Item className={cn(itemClass, "justify-between gap-6")}>
        Sort by
        <IconChevronRightMedium className="size-4 text-gray-1000" />
      </ContextMenu.Item>
      <ContextMenu.Item className={itemClass}>Clean up</ContextMenu.Item>
      <ContextMenu.Item className={itemClass}>
        Show view options
      </ContextMenu.Item>
    </>
  );
}

function RightClickArea({
  animated,
  label,
}: {
  animated: boolean;
  label: string;
}) {
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger className="flex min-h-56 w-full select-none flex-col items-center justify-center gap-4 rounded-lg border border-gray-400 border-dashed bg-gray-100 text-center text-gray-1000">
        <Badge className="px-4 text-gray-1200" mono={false}>
          {label}
        </Badge>
        <ResultBadge bad={animated} />
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Positioner className="outline-none">
          <ContextMenu.Popup
            className={cn(
              POPUP_BASE,
              animated ? POPUP_ANIMATED : POPUP_EXIT_ONLY
            )}
          >
            <MenuItems animated={animated} />
          </ContextMenu.Popup>
        </ContextMenu.Positioner>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}

export default function AnimationSkipEnterDemo() {
  return (
    <DemoWrapper margin="">
      <DemoFooter position="top">
        <DemoHint>
          <span className="pointer-coarse:hidden">
            Right-click in each area
          </span>
          <span className="pointer-coarse:inline hidden">
            Tap and hold in each area
          </span>
        </DemoHint>
      </DemoFooter>
      <div className="grid w-full grid-cols-1 gap-1 p-1 sm:grid-cols-2">
        <RightClickArea animated label="Enter and exit" />
        <RightClickArea animated={false} label="Exit only" />
      </div>
    </DemoWrapper>
  );
}
